import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { PublicProduct } from '@/types/checkout'
import { CheckoutPanel } from './CheckoutPanel'

interface ProductPurchaseDialogProps {
  product: PublicProduct | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** After "Done" on a paid (or free) purchase: the offer of a padlocked space opens it. */
  onPurchased?: () => void
}

/**
 * Buying one product, in a modal over whatever listed it.
 *
 * Controlled from outside (`open` / `onOpenChange`) so the list that opens it
 * — the catalog page today, a storefront space later — decides when.
 */
export function ProductPurchaseDialog({ product, open, onOpenChange, onPurchased }: ProductPurchaseDialogProps) {
  const { t } = useTranslation('checkout')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6">
        {product && (
          <div className="min-w-0 space-y-5">
            <DialogHeader className="pr-8 text-left">
              <DialogTitle className="leading-snug">{product.title}</DialogTitle>
              {product.shortDescription ? (
                <DialogDescription className="text-gray-600">{product.shortDescription}</DialogDescription>
              ) : (
                <DialogDescription className="sr-only">{t('purchase.dialogDescription')}</DialogDescription>
              )}
            </DialogHeader>
            <CheckoutPanel
              product={product}
              lineLabel={product.title}
              submitLabel={t('purchase.buy')}
              onDone={() => {
                onOpenChange(false)
                onPurchased?.()
              }}
              doneLabel={t('purchase.outcome.close')}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
